import { fetchPolygonAreasHa } from '../../../../../api/services/ad';

import { draftGeometryToFeatures, isNonEmptyAdGeometry } from './api';

import {
  fellingApplicationKeyFromFeature,
  nextSellTimberSectionId,
  roundHa,
  SELL_TIMBER_EXTRA_SPECIES
} from './sellTimberShared';

/**
 * Builds GeoJSON Features for `/map-api-data/` and for summary-map labels — same order:
 * selected SBP-avdelningar → avverkningsanmälningar → ritade polygoner → annonsutkast geometry.
 * Placed markers are excluded (no area for virkesdata).
 *
 * @param {object | null | undefined} sel
 * @param {unknown} [draftGeometryFallback] — e.g. `hydratedAdGeometry` when `sel.customAreaFromAd` is absent (SellTimberMap options).
 */
export function collectFeaturesFromMapSelection(sel, draftGeometryFallback = null) {
  if (!sel) return [];
  const {
    sections = [],
    selectedSectionLocalIds = [],
    fellingApplicationsGeojson,
    selectedFellingApplicationIds = [],
    drawnPolygons = []
  } = sel;

  const out = [];

  for (const localKey of selectedSectionLocalIds) {
    const sect = sections.find((s) => String(s.localID) === String(localKey));
    if (sect?.geom) {
      out.push({
        type: 'Feature',
        geometry: sect.geom,
        properties: {
          name: `Vald avdelning ${localKey}`,
          source: 'section',
          /** Skogsbruksplan / property bundle avdelning — step 3 can read virkesdata from this object instead of `/map-api-data/`. */
          localID: String(localKey)
        }
      });
    }
  }

  const fellingFeatures =
    fellingApplicationsGeojson?.type === 'FeatureCollection'
      ? fellingApplicationsGeojson.features
      : fellingApplicationsGeojson?.type === 'Feature'
        ? [fellingApplicationsGeojson]
        : [];

  fellingFeatures.forEach((feature, index) => {
    const key = fellingApplicationKeyFromFeature(feature, index);
    if (!selectedFellingApplicationIds.includes(key)) return;
    out.push({
      ...feature,
      properties: {
        ...(feature.properties || {}),
        source: 'felling',
        name: `Avverkning ${key}`
      }
    });
  });

  drawnPolygons.forEach((feature, i) => {
    if (!feature?.geometry) return;
    out.push({
      ...feature,
      properties: {
        ...(feature.properties || {}),
        source: 'drawn',
        name: feature.properties?.name ?? `Ritad avdelning #${i + 1}`
      }
    });
  });

  const customArea = sel.customAreaFromAd ?? draftGeometryFallback;
  if (isNonEmptyAdGeometry(customArea) && customArea) {
    draftGeometryToFeatures(customArea).forEach((f, i) => {
      const t = f.geometry?.type;
      if (t !== 'Polygon' && t !== 'MultiPolygon') return;
      out.push({
        ...f,
        properties: {
          ...(f.properties || {}),
          source: 'draft',
          name: f.properties?.name ?? `Annonsutkast #${i + 1}`
        }
      });
    });
  }

  return out;
}

/**
 * One table row per map feature from step 2: name + areal only; volym/TGL left empty for the user.
 * Order matches {@link collectFeaturesFromMapSelection}. Areal for polygons comes from the property
 * bundle when present, otherwise POST {@link fetchPolygonAreasHa}.
 *
 * @param {object | null | undefined} sel
 * @param {unknown} [draftGeometryFallback]
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function buildSkeletonVirkesRowsFromMapSelection(sel, draftGeometryFallback = null) {
  const features = collectFeaturesFromMapSelection(sel, draftGeometryFallback);
  const bundleSections = sel?.sections ?? [];

  /** @type {(number | null)[]} */
  const areaHaByFeatureIndex = features.map(() => null);

  for (let fi = 0; fi < features.length; fi++) {
    const feature = features[fi];
    const props = feature?.properties;
    if (props?.source === 'section' && props.localID != null) {
      const sect = bundleSections.find((s) => String(s.localID) === String(props.localID));
      if (sect && sect.area != null && sect.area !== '') {
        areaHaByFeatureIndex[fi] = roundHa(sect.area, { invalidAsZero: true });
      }
    }
  }

  const geometriesForApi = [];
  /** @type {number[]} */
  const apiSlotToFeatureIndex = [];
  for (let fi = 0; fi < features.length; fi++) {
    if (areaHaByFeatureIndex[fi] != null) continue;
    const g = features[fi]?.geometry;
    if (!g) continue;
    geometriesForApi.push(g);
    apiSlotToFeatureIndex.push(fi);
  }

  /** @type {(number | null)[]} */
  let areasFromApi = [];
  if (geometriesForApi.length > 0) {
    const resp = await fetchPolygonAreasHa(geometriesForApi);
    if (resp.ok && Array.isArray(resp.data?.areas_ha)) {
      areasFromApi = resp.data.areas_ha;
    } else {
      areasFromApi = new Array(geometriesForApi.length).fill(null);
    }
  }

  for (let j = 0; j < apiSlotToFeatureIndex.length; j++) {
    const fi = apiSlotToFeatureIndex[j];
    const ha = areasFromApi[j];
    if (ha != null && Number.isFinite(ha)) {
      areaHaByFeatureIndex[fi] = ha;
    }
  }

  const rows = [];

  for (let fi = 0; fi < features.length; fi++) {
    const feature = features[fi];
    const props = feature?.properties;
    const nameFromMap =
      props?.name != null && String(props.name) !== '' ? String(props.name) : '';
    const rawArea = areaHaByFeatureIndex[fi];
    const areaHa = rawArea != null && Number.isFinite(rawArea) ? rawArea : 0;

    const index = rows.length;
    const name = nameFromMap || `Avdelning ${index + 1}`;
    rows.push({
      id: nextSellTimberSectionId(rows),
      name,
      area: areaHa,
      volumeM3sk: 0,
      volumeM3fub: 0,
      tall: 0,
      gran: 0,
      lov: 0,
      ...Object.fromEntries(SELL_TIMBER_EXTRA_SPECIES.map(({ key }) => [key, '']))
    });
  }

  return rows;
}
